import pptxgen from 'pptxgenjs';
import { sanitizeHeaderText, downloadFilenameGeneratorForPPT } from '.';
import { IRelevantFiles } from '../../../models/IQueryType';

import LABELS from '../../../ui-constants/en.json';
import { decodeHTML } from '../../../utils/commonUtils';

export type pptDataType = {
  userQueryWithResponse: any;
  checkedCheckboxes?: any;
  eyIPInterRadio?: any;
  validURLinputValue?: any;
};
export enum BulletPointType {
  hyphen = '-',
  dot = '•',
  number = 'number',
  NA = 'NA'
}
export const exportToPptTranscript = (
  userQueryWithResponse: any,
  checkedCheckboxes?: any,
  eyIPInterRadio?: any,
  validURLinputValue?: any
) => {
  const { user, SEResponse } = userQueryWithResponse;
  const sourceTable = [
    {
      text: `Source: Expert Call Transcripts (DBS)`,
      options: {
        border: { type: 'none' },
        colspan: 2,
        margin: [10, 0, 10, 0],
        fill: {
          color: '#ffffff'
        }
      }
    }
  ];
  try {
    let presentation = new pptxgen();
    let rows: pptxgen.TableRow[] = [];
    const countSpacesBeforeHyphen = (input: string, bulletPointType: BulletPointType): number => {
      let indexOfHyphen = 0;
      if (bulletPointType === BulletPointType.hyphen) {
        // indexOfHyphen = input.indexOf('-');
        const match = input.match(/^(\s*)-/);
        return match ? match[1].length : 0;
      }
      if (bulletPointType === BulletPointType.dot) {
        indexOfHyphen = input.indexOf('•');
      }

      if (bulletPointType === BulletPointType.number) {
        const match = input.match(/^(\s*)/); // Matches leading whitespace characters
        return match ? match[1].length : 0;
      }
      const substringBeforeHyphen = input.slice(0, indexOfHyphen);
      const spaceCount = (substringBeforeHyphen.match(/ /g) || []).length;
      return spaceCount;
    };

    const isNumericBullet = (sentence: string): boolean => {
      const numericBulletPattern = /^\s*\d+[\.\)\-]\s+/;
      return numericBulletPattern.test(sentence);
    };

    const handleRowCreation = (
      data: string,
      hasBulletpoint: boolean,
      isBold: boolean,
      indentLevel: number,
      isExtraSpace: boolean
    ): pptxgen.TextProps[] => {
      let dynamicTextArray: pptxgen.TextProps[] = [];
      if (hasBulletpoint) {
        dynamicTextArray.push({
          text: '.',
          options: {
            fontSize: 10,
            bold: isBold,
            indentLevel: indentLevel,
            bullet: true,
            align: 'left',
            color: 'FFFFFF'
          }
        });
        dynamicTextArray.push({
          text: data,
          options: {
            fontSize: 10,
            bold: isBold,
            italic: false
          }
        });
      } else {
        if (!isExtraSpace) {
          dynamicTextArray.push({
            text: '.',
            options: {
              fontSize: 10,
              bold: isBold,
              italic: false,
              color: 'FFFFFF'
            }
          });
        }
        dynamicTextArray.push({
          text: data,
          options: {
            fontSize: 10,
            bold: isBold,
            italic: false
          }
        });
      }
      return dynamicTextArray;
    };
    if (SEResponse) {
      const userQuery = user && decodeHTML(user?.summary);
      const rawSESummary = SEResponse?.summary;

      const headerPattern1 = /^#\s+(.+)$/gm;
      const headerPattern2 = /^##\s+(.+)$/gm;
      const headerPattern3 = /^###\s+(.+)$/gm;
      const headerPattern4 = /^####\s+(.+)$/gm;
      const headerPattern5 = /^#####\s+(.+)$/gm;
      const boldPattern = /\*\*(.+?)\*\*/g;
      const { relevantFiles } = SEResponse;

      const splitlines = rawSESummary?.split('\n') || [];
      for (let i = 0; i < splitlines.length; i++) {
        let line = splitlines[i];
        let formattedLine = line; //
        if (headerPattern1.test(line)) {
          line = line.replace('#', '');
          rows.push([{ text: line, options: { bold: true, color: '656579' } }]);
          // rows.push([{ text: "", options: {} }]); // Add empty row
        }
        // Check and apply header pattern 2
        else if (headerPattern2.test(line)) {
          line = line.replace('##', '');
          rows.push([{ text: line, options: { bold: true, color: '656579' } }]);
          // rows.push([{ text: "", options: {} }]); // Add empty row
        }
        // Check and apply header pattern 3
        else if (headerPattern3.test(line)) {
          line = line.replace('###', '');
          rows.push([{ text: line, options: { bold: true, color: '656579' } }]);
          // rows.push([{ text: "", options: {} }]); // Add empty row
        }
        // Check and apply header pattern 4
        else if (headerPattern4.test(line)) {
          line = line.replace('####', '');
          rows.push([{ text: line, options: { bold: true, color: '656579' } }]);
          // rows.push([{ text: "", options: {} }]); // Add empty row
        }
        // Check and apply header pattern 5
        else if (headerPattern5.test(line)) {
          line = line.replace('#####', '');
          rows.push([{ text: line, options: { bold: true, color: '656579' } }]);
          // rows.push([{ text: "", options: {} }]); // Add empty row
        } else {
          if (line.trim().length > 0) {
            let currentIndentLevel = 0;
            let isNumericbulletpoint = isNumericBullet(line);
            let startsWithHyphen = line.trim().startsWith('-');
            if (isNumericbulletpoint) {
              currentIndentLevel = countSpacesBeforeHyphen(line, BulletPointType.number);
            } else {
              currentIndentLevel = countSpacesBeforeHyphen(line, BulletPointType.hyphen);
            }

            currentIndentLevel = currentIndentLevel > 0 ? currentIndentLevel - 1 : 0;

            line = line.replace(/^\s*-/, '• ');
            const updatedText = line.replace(/\*\*(.*?)\*\*/g, '**[Bold]$1[/Bold]**');
            const splitText = updatedText.split(/\*\*/);
            for (let i = splitText.length - 1; i >= 0; i--) {
              if (splitText[i].trim() === '•') {
                splitText.splice(i, 1); // Remove that specific element
              }
            }

            let dynamicTextArray: pptxgen.TextProps[] = [];
            for (let j = 0; j < splitText.length; j++) {
              const isBold = splitText[j].includes('[Bold]');

              if (isNumericBullet(splitText[1])) {
                splitText[j + 1].replace('[Bold]', '[Bold]' + splitText[1]);
              } else {
                if (startsWithHyphen && isBold) {
                  splitText[j] = '• ' + splitText[j];
                }

                if (splitText[j].trim().length > 0) {
                  // Check if text contains [Bold]
                  let cleanedText = splitText[j].replace('[Bold]', '').replace('[/Bold]', ' ');
                  let isExtraSpace = false;
                  if (cleanedText == updatedText) {
                    isExtraSpace = true;
                  }

                  let textprops = handleRowCreation(
                    cleanedText,
                    isNumericbulletpoint ? isNumericbulletpoint : startsWithHyphen,
                    isBold,
                    currentIndentLevel,
                    isExtraSpace
                  );
                  // Check if textprops is an array and handle accordingly
                  if (Array.isArray(textprops)) {
                    dynamicTextArray.push(...textprops); // Spread and push all elements
                  } else {
                    dynamicTextArray.push(textprops); // Push single object
                  }
                }
                startsWithHyphen = false;
                isNumericbulletpoint = false;
              }
            }

            rows.push([{ text: dynamicTextArray }]);
          }
        }
      }

      if (userQuery) {
        rows.unshift([
          { text: `${userQuery}`, options: { bold: true, fontSize: 12, color: '000000' } }
        ]);
      }
      if (!relevantFiles) {
         if (SEResponse?.isParitallyAbortedResponse) {
          rows.push([
            {
              text: LABELS.chat_reply.PARTIAL_STOP_GENERATION_MSG,
              options: { color: '656579', italic: true }
            }
          ]);
        }
        rows.push(sourceTable as pptxgen.TableRow);
       }
      const maxRowsPerSlide = 12;
      const slidesRequired = Math.ceil(rows.length / maxRowsPerSlide); // Calculate the number of slides needed

      for (let i = 0; i < slidesRequired; i++) {
        const startRow = i * maxRowsPerSlide;
        const endRow = startRow + maxRowsPerSlide;
        const rowsToAdd = rows.slice(startRow, endRow);
        const dynamicHeight =
          rowsToAdd.length < maxRowsPerSlide ? (rowsToAdd.length / maxRowsPerSlide) * 8.5 : 8.6;
        presentation
          .addSlide()
          .addTable(rowsToAdd, { autoPage: false, h: dynamicHeight, rowH: 0.1, fontSize: 10 });
      }

      if (
        relevantFiles &&
        relevantFiles.length > 0 &&
        relevantFiles.every((el: any) => Boolean(el) && typeof el === 'object')
      ) {
        const documentTableRows: pptxgen.TableRow[] = [];
        const docuementTableHead: pptxgen.TableCell[] = [
          {
            text: sanitizeHeaderText('Relevant Document(s)'),
            options: { bold: true }
          },
          {
            text: sanitizeHeaderText('Author(s)'),
            options: { bold: true }
          }
        ];
        documentTableRows.push(docuementTableHead);
        relevantFiles.forEach((el: Partial<IRelevantFiles>) => {
          const rowValues: pptxgen.TableCell[] = [];
          const filesName = el.file_name;
          let filesPage = el?.page_number && el.page_number.length > 1 ? '(Slides ' : '(Slide ';

          el.page_number?.forEach((slide: string, i) => {
            if (el?.page_number && el.page_number.length > i + 1) {
              filesPage = filesPage + slide + ', ';
            } else {
              filesPage = filesPage + slide + ')';
            }
          });
          let authorData: string = '';
          if (el.authors) {
            authorData = el.authors.reduce((str, auth) => {
              if (str === '') {
                str = auth?.author_name;
              } else {
                str = str + ', ' + auth?.author_name;
              }
              return str;
            }, '');
          }

          rowValues.push({
            text: filesName + '\n' + filesPage
          });

          rowValues.push({
            text: authorData
          });
          documentTableRows.push(rowValues);
        });

        if (SEResponse?.isParitallyAbortedResponse) {
          documentTableRows.push([
            {
              text: LABELS.chat_reply.PARTIAL_STOP_GENERATION_MSG,
              options: { color: '656579', italic: true }
            }
          ]);
        }

        documentTableRows.push(sourceTable as pptxgen.TableRow);
        presentation.addSlide().addTable(documentTableRows, {
          border: { color: 'e8e8ee', pt: 1, type: 'solid' },
          fill: {
            color: 'f7f7f8'
          },
          autoPage: true,
          autoPageCharWeight: -0.5,
          autoPageLineWeight: 0.5
        });
      }
    }
    presentation
      .write({ outputType: 'blob' })
      .then((file: any) => {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(file as Blob);
        link.download = downloadFilenameGeneratorForPPT(
          checkedCheckboxes,
          eyIPInterRadio,
          validURLinputValue,
          'ppt'
        );
        link.click();
      })
      .catch((err: any) => console.log('::PPT catch errr', err));
  } catch (err) {
    console.log('::::PPT error', err);
  }
};
